import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { brand, buildTrackingApiUrl } from '../config/brand';
import { TrackingResult } from '../components/TrackingPanel';
import faberlicLogo from '../AboutMain/Partner/components/media/Faberlic-HD.png';
import { useLanguage } from '../i18n/LanguageContext';

function Home() {
  const { language, t } = useLanguage();
  const [code, setCode] = useState('');
  const [trackingStatus, setTrackingStatus] = useState('idle');
  const [trackingMessage, setTrackingMessage] = useState('');
  const [destination, setDestination] = useState(null);

  const closeTracking = () => {
    setDestination(null);
    setTrackingStatus('idle');
  };

  useEffect(() => {
    if (trackingStatus !== 'success') return undefined;
    const handleEscape = (event) => {
      if (event.key === 'Escape') closeTracking();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [trackingStatus]);

  const handleTracking = async (event) => {
    event.preventDefault();
    const value = code.trim();
    if (!value) {
      setTrackingStatus('error');
      setTrackingMessage('trackingRequired');
      setDestination(null);
      return;
    }

    setTrackingStatus('loading');
    setTrackingMessage('');
    setDestination(null);

    try {
      const response = await fetch(buildTrackingApiUrl(value, language));
      const data = await response.json();
      if (!response.ok || data.error || !data.destination) throw new Error('not_found');
      setCode(value);
      setDestination(data.destination);
      setTrackingStatus('success');
    } catch (_error) {
      setTrackingStatus('error');
      setTrackingMessage('trackingNotFound');
    }
  };

  return (
    <>
      <section className="hero">
        <div className="hero-glow hero-glow--one" />
        <div className="hero-glow hero-glow--two" />
        <div className="shell hero-grid">
          <div className="hero-copy">
            <Link to="/contacts" className="hero-note">
              {t.home.businessQuestion} <strong>{t.home.discussTask}</strong>
            </Link>
            <p className="eyebrow">{t.home.eyebrow}</p>
            <h1>{t.home.titleBefore} <em>{t.home.titleEmphasis}</em></h1>
            <p className="hero-lead">{t.home.lead}</p>

            <form className="hero-tracking" onSubmit={handleTracking}>
              <label htmlFor="hero-tracking-code">{t.home.trackLabel}</label>
              <div>
                <input
                  id="hero-tracking-code"
                  value={code}
                  onChange={(event) => {
                    setCode(event.target.value);
                    if (trackingStatus === 'error') {
                      setTrackingStatus('idle');
                      setTrackingMessage('');
                    }
                  }}
                  placeholder={t.home.trackPlaceholder}
                  autoComplete="off"
                />
                <button className="button button--primary" type="submit" disabled={trackingStatus === 'loading'}>
                  {trackingStatus === 'loading' ? t.common.searching : t.home.check}
                </button>
              </div>
              {trackingStatus === 'error' && (
                <p className="hero-tracking__feedback" role="alert">
                  <span aria-hidden="true">!</span>{t.home[trackingMessage]}
                </p>
              )}
            </form>

            <div className="hero-actions">
              <a className="button button--secondary" href={brand.cabinetUrl} target="_blank" rel="noreferrer">
                {t.common.cabinet} <span aria-hidden="true">↗</span>
              </a>
              <a className="text-link" href={brand.contact.phoneHref}>{brand.contact.phone}</a>
            </div>
          </div>

          <figure className="hero-photo">
            <img
              src="/hero-courier-v2.jpg"
              alt={t.home.heroAlt}
            />
            <figcaption>
              <span>Beta Express / Kazakhstan</span>
              <strong>{t.home.doorToDoor}</strong>
            </figcaption>
          </figure>
        </div>
      </section>

      {trackingStatus === 'success' && destination && (
        <div className="tracking-modal" role="dialog" aria-modal="true" aria-labelledby="tracking-modal-title">
          <button className="tracking-modal__backdrop" type="button" onClick={closeTracking} aria-label={t.home.closeResult} />
          <div className="tracking-modal__window">
            <div className="tracking-modal__bar">
              <div>
                <span className="eyebrow">{t.home.resultEyebrow}</span>
                <h2 id="tracking-modal-title">{t.home.resultTitle}</h2>
              </div>
              <button className="tracking-modal__close" type="button" onClick={closeTracking} aria-label={t.common.close}>×</button>
            </div>
            <div className="tracking-modal__body">
              <TrackingResult destination={destination} />
            </div>
          </div>
        </div>
      )}

      <section className="trust-strip">
        <div className="shell trust-strip__inner">
          {t.home.trust.map((item) => <span key={item}>{item}</span>)}
        </div>
      </section>

      <section className="section shell" id="services">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{t.home.servicesEyebrow}</p>
            <h2>{t.home.servicesTitle}</h2>
          </div>
          <Link to="/services" className="text-link">{t.home.allServices}</Link>
        </div>
        <div className="service-grid">
          {t.home.serviceCards.map((service, index) => (
            <article className="service-card" key={service.title}>
              <span className="service-card__number">{String(index + 1).padStart(2, '0')}</span>
              <h3>{service.title}</h3>
              <p>{service.text}</p>
              <Link to="/contacts" aria-label={`${t.home.learnMore}: ${service.title}`}>{t.common.discussDelivery} <span>→</span></Link>
            </article>
          ))}
        </div>
      </section>

      <section className="section section--light">
        <div className="shell partner-layout">
          <div>
            <p className="eyebrow eyebrow--dark">{t.home.partnerEyebrow}</p>
            <h2>{t.home.partnerTitle}</h2>
          </div>
          <div className="partner-card">
            <img className="partner-card__logo" src={faberlicLogo} alt="Faberlic" />
            <p>{t.home.partnerText}</p>
          </div>
        </div>
      </section>

      <section className="section shell">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{t.home.stepsEyebrow}</p>
            <h2>{t.home.stepsTitle}</h2>
          </div>
        </div>
        <ol className="steps-grid">
          {t.home.steps.map((step, index) => (
            <li key={step.title}><span>{String(index + 1).padStart(2, '0')}</span><h3>{step.title}</h3><p>{step.text}</p></li>
          ))}
        </ol>
      </section>

      <section className="section shell">
        <div className="cta-panel">
          <div>
            <p className="eyebrow">{t.home.ctaEyebrow}</p>
            <h2>{t.home.ctaTitle}</h2>
            <p>{t.home.ctaText}</p>
          </div>
          <Link to="/contacts" className="button button--primary">{t.home.ctaButton}</Link>
        </div>
      </section>
    </>
  );
}

export default Home;
